import { Badge, Box, IconButton, styled } from '@mui/material'
import { buildMask, useOverflowState } from 'components/common/hooks/useOverflowState'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Icon as Iconify } from '@iconify/react'
import { appColors } from 'utils'

const DEFAULT_TAB_ICON = 'vscode-icons:default-file'

const TAB_HEIGHT = 30
const CHEVRON_W = 18
const ADD_TAB_WIDTH = 26

const sx = {
  full: { height: '100%', width: '100%' },

  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },

  tabStrip: (background) => ({
    backgroundColor: background || 'transparent',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    height: TAB_HEIGHT,
    minHeight: TAB_HEIGHT,
    paddingX: 2,
  }),

  tabRowBase: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0.5,
    width: '100%',
    height: TAB_HEIGHT,
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },

  tabButton: (selected, disabled, condensed, style) => {
    const selectedTextColor = style?.selectedTextColor || '#f0f0f0'
    const defaultTextColor = style?.defaultTextColor || '#f0f0f0'
    const selectedBackgroundColor = style?.selectedBackgroundColor || 'transparent'
    const defaultBackgroundColor = style?.defaultBackgroundColor || style?.unselectedTabBackgroundColor || 'transparent'
    const selectedTabBorderColor = style?.selectedTabBorderColor || 'rgba(255,255,255,0.09)'
    const unselectedTabBorderColor = style?.unselectedTabBorderColor || 'rgba(255,255,255,0.05)'

    const color = disabled ? '#919191' : selected ? selectedTextColor : defaultTextColor

    const hoverColor = disabled
      ? '#919191'
      : selected
        ? selectedTextColor
        : style?.defaultTextColor
          ? `${style.defaultTextColor}CC`
          : '#b0d0eb'

    return {
      px: condensed ? 0.75 : 1.25,
      height: TAB_HEIGHT,
      minHeight: TAB_HEIGHT,
      boxSizing: 'border-box',
      cursor: disabled ? 'not-allowed' : 'pointer',
      color,
      whiteSpace: 'nowrap',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: selected ? selectedBackgroundColor : defaultBackgroundColor,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      border: selected ? `1px solid ${selectedTabBorderColor}` : `1px solid ${unselectedTabBorderColor}`,
      borderBottom: '1px solid transparent',
      textShadow: selected ? `1px 0 ${selectedTextColor}` : 'none',
      transition:
        'color 150ms ease, max-width 150ms ease, padding 150ms ease, opacity 150ms ease, box-shadow 150ms ease',

      '&:hover': {
        color: hoverColor,
        borderBottom: disabled ? '1px solid transparent' : '1px solid gray',
      },

      '& .tab-label': {
        ...sx.ellipsis,
        maxWidth: condensed && !selected ? 0 : 200,
        opacity: condensed && !selected ? 0 : 1,
        marginLeft: condensed && !selected ? 0 : 1,
        transition: 'max-width 200ms ease, opacity 160ms ease, margin-left 160ms ease',
      },

      '& .status-unlock': { display: 'flex' },
      '& .tab-duplicate': { display: 'none' },
      '&:hover .status-unlock': { display: 'none' },
      '&:hover .status-pencil': { display: 'flex' },
      '&:hover .tab-duplicate': { display: 'flex' },
    }
  },

  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    marginLeft: 0.5,
    flexShrink: 0,
    borderRadius: '3px',
    transition: 'opacity 120ms ease, background-color 120ms ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
      color: appColors.nptColor,
    },
  },

  editIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    marginRight: 0.5,
    flexShrink: 0,
    borderRadius: '3px',
    transition: 'opacity 120ms ease, background-color 120ms ease',
  },

  editIconClickable: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  },

  duplicateButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    marginLeft: 0.5,
    flexShrink: 0,
    borderRadius: '3px',
    transition: 'opacity 120ms ease, background-color 120ms ease',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  },

  addTabButton: (style) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: ADD_TAB_WIDTH,
    height: TAB_HEIGHT,
    minHeight: TAB_HEIGHT,
    flexShrink: 0,
    cursor: 'pointer',
    color: style?.defaultTextColor || '#f0f0f0',
    opacity: 0.7,
    borderRadius: 1,
    transition: 'opacity 120ms ease, background-color 120ms ease',
    '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.08)' },
  }),

  renameInput: {
    font: 'inherit',
    color: 'inherit',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    borderBottom: '1px solid currentColor',
    padding: 0,
  },
}

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    bottom: 5,
    right: 2,
    fontSize: '.6rem',
    height: '14px',
    minWidth: '14px',
    padding: 0,
  },
}))

function TabEditIcon({ isEditable, canRename, onStartRename }) {
  if (!isEditable) {
    return (
      <Box sx={{ ...sx.editIcon }}>
        <Iconify icon='mdi:lock' width={14} height={14} />
      </Box>
    )
  }

  if (canRename) {
    return (
      <Box sx={sx.editIcon}>
        <Box
          onClick={(e) => {
            e.stopPropagation()
            onStartRename()
          }}
          sx={sx.editIconClickable}>
          <Iconify icon='mdi:pencil-outline' width={14} height={14} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ ...sx.editIcon, opacity: 0.4 }}>
      <Iconify icon='mdi:lock-open-variant' width={14} height={14} />
    </Box>
  )
}

function TabButton({
  selected,
  tab,
  onClick,
  condensed = false,
  style = {},
  showEditState = false,

  isRenaming = false,
  onStartRename,
  onCommitRename,
  onCancelRename,

  onCloseTab,
  onDuplicateTab,

  canDrag = false,
  isDragging = false,
  isDropTarget = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDragLeave,
}) {
  const icon = tab.icon || (condensed ? DEFAULT_TAB_ICON : null)
  const label = tab.label ?? tab.name ?? tab.key
  const badgeContent = tab.badgeContent

  const isEditable = tab.editable !== false
  const canRename = typeof onStartRename === 'function' && isEditable
  const canClose = typeof onCloseTab === 'function' && isEditable
  const canDuplicate = typeof onDuplicateTab === 'function'
  const showEditStatus = showEditState && !(condensed && !selected) && !isRenaming

  const [draftName, setDraftName] = useState(label)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isRenaming) setDraftName(label)
  }, [isRenaming, label])

  useEffect(() => {
    if (!isRenaming) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isRenaming])

  const commitRename = () => {
    const trimmed = draftName.trim()
    if (trimmed && trimmed !== label) {
      onCommitRename(tab.key, trimmed)
    } else {
      onCancelRename()
    }
  }

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') commitRename()
    if (e.key === 'Escape') onCancelRename()
  }

  const dragStyles = {
    ...(isDragging && { opacity: 0.35, boxShadow: 'none' }),
    ...(isDropTarget && {
      boxShadow: 'inset 0 0 0 1px rgba(100,180,255,0.5)',
      backgroundColor: 'rgba(100,180,255,0.08)',
    }),
    ...(canDrag && !isRenaming && { cursor: 'grab' }),
    ...(isDragging && { cursor: 'grabbing' }),
  }

  return (
    <Box
      sx={{ ...sx.tabButton(selected, tab.disabled, condensed, style), ...dragStyles }}
      draggable={canDrag && !isRenaming}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onDragLeave={onDragLeave}
      onClick={tab.disabled || isRenaming ? undefined : onClick}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
        {showEditStatus && (
          <TabEditIcon isEditable={isEditable} canRename={canRename} onStartRename={() => onStartRename(tab.key)} />
        )}

        {(icon || tab.iconNode) && (
          <StyledBadge
            color='error'
            badgeContent={badgeContent > 0 ? badgeContent : null}
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {typeof icon === 'string' ? <Iconify icon={icon} style={{ width: 20, height: 20 }} /> : icon}
              {tab.iconNode && (
                <img
                  alt={`${tab.label} icon`}
                  src={selected ? tab.activeIcon || tab.iconNode : tab.iconNode}
                  style={{ width: 20, height: 20 }}
                />
              )}
            </Box>
          </StyledBadge>
        )}

        {isRenaming ? (
          <Box
            component='input'
            ref={inputRef}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={commitRename}
            onKeyDown={handleRenameKeyDown}
            sx={{ ...sx.renameInput, width: `${Math.max(6, draftName.length + 1)}ch`, marginLeft: 1 }}
          />
        ) : (
          <Box className='tab-label'>{label}</Box>
        )}
      </Box>

      {canDuplicate && !isRenaming && (
        <Box
          className='tab-duplicate'
          onClick={(e) => {
            e.stopPropagation()
            onDuplicateTab(tab.key)
          }}
          sx={sx.duplicateButton}
          title='Duplicate'>
          <Iconify icon='mdi:content-duplicate' width={12} height={12} />
        </Box>
      )}

      {canClose && !isRenaming && (
        <Box
          className='tab-close'
          onClick={(e) => {
            e.stopPropagation()
            onCloseTab(tab.key)
          }}
          sx={sx.closeButton}>
          <Iconify icon='fa-regular:trash-alt' width={12} height={12} />
        </Box>
      )}
    </Box>
  )
}

export default function Tabs({
  selectedEditor,
  setSelectedEditor,
  tabs,
  condensed = false,
  style = {
    defaultBackgroundColor: '',
    defaultTextColor: '',
    selectedBackgroundColor: '',
    selectedTabBorderColor: '',
    selectedTextColor: '',
    unselectedTabBackgroundColor: '',
    unselectedTabBorderColor: '',
  },

  // Pass any of these to opt a tab strip into editing.
  // Individual tabs can opt out of editing by setting `editable: false`.
  // Individual tabs can opt out of reordering by setting `fixPosition: true`.
  onRename,
  onClose,
  onAdd,
  onReorder,
  onDuplicate,
}) {
  const { ref, hasOverflow, atStart, atEnd } = useOverflowState([tabs])
  const [hover, setHover] = useState(false)
  const [renamingKey, setRenamingKey] = useState(null)
  const [draggedKey, setDraggedKey] = useState(null)
  const [dropTargetKey, setDropTargetKey] = useState(null)

  const isEditableStrip = !!(onRename || onClose || onAdd || onReorder)
  const mask = useMemo(() => buildMask(!atStart, !atEnd), [atStart, atEnd])

  const scrollByAmount = (dir) => {
    const el = ref.current
    if (!el) return
    const amount = Math.max(180, Math.floor(el.clientWidth * 0.8))
    el.scrollBy({ left: dir * amount, behavior: 'smooth' })
  }

  const handleCommitRename = (key, name) => {
    setRenamingKey(null)
    onRename?.(key, name)
  }

  const handleCancelRename = () => setRenamingKey(null)

  const handleDragStart = useCallback((e, key) => {
    setDraggedKey(key)
    e.dataTransfer.effectAllowed = 'move'

    const ghost = document.createElement('div')
    ghost.style.opacity = '0'
    document.body.appendChild(ghost)
    e.dataTransfer.setDragImage(ghost, 0, 0)
    requestAnimationFrame(() => document.body.removeChild(ghost))
  }, [])

  const handleDragOver = useCallback((e, key) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropTargetKey(key)
  }, [])

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      if (!draggedKey || !dropTargetKey || draggedKey === dropTargetKey) {
        setDraggedKey(null)
        setDropTargetKey(null)
        return
      }

      const fromIndex = tabs.findIndex((t) => t.key === draggedKey)
      const toIndex = tabs.findIndex((t) => t.key === dropTargetKey)

      if (fromIndex !== -1 && toIndex !== -1) {
        onReorder?.(fromIndex, toIndex)
      }

      setDraggedKey(null)
      setDropTargetKey(null)
    },
    [draggedKey, dropTargetKey, tabs, onReorder],
  )

  const handleDragEnd = useCallback(() => {
    setDraggedKey(null)
    setDropTargetKey(null)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDropTargetKey(null)
  }, [])

  return (
    <Box sx={sx.tabStrip(style.defaultBackgroundColor)}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          height: '100%',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}>
        {hasOverflow && !atStart && (
          <Box
            sx={{
              position: 'absolute',
              left: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: CHEVRON_W,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
            }}>
            <IconButton
              size='small'
              onClick={() => scrollByAmount(-1)}
              disabled={!hasOverflow || atStart}
              sx={{
                opacity: hasOverflow ? (hover ? 1 : 0.6) : 0,
                visibility: hasOverflow && !atStart ? 'visible' : 'hidden',
                transition: 'opacity 120ms',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: 'transparent' },
              }}>
              <Iconify icon='lucide:chevron-left' />
            </IconButton>
          </Box>
        )}

        <Box
          ref={ref}
          sx={{
            ...sx.tabRowBase,
            overflow: 'auto',
            scrollbarWidth: 'none',
            WebkitMaskImage: mask,
            maskImage: mask,
            paddingRight: condensed ? 15 : 0,
          }}>
          {tabs.map((tab) => {
            const canDrag = !!onReorder && !tab.fixPosition
            const canBeDropTarget = !!onReorder && !tab.fixPosition && tab.key !== draggedKey

            return (
              <TabButton
                key={tab.key}
                tab={tab}
                condensed={condensed}
                selected={selectedEditor === tab.key}
                style={style}
                showEditState={isEditableStrip}
                isRenaming={renamingKey === tab.key}
                onStartRename={onRename ? setRenamingKey : undefined}
                onCommitRename={handleCommitRename}
                onCancelRename={handleCancelRename}
                onCloseTab={onClose}
                onDuplicateTab={onDuplicate}
                onClick={() => setSelectedEditor(tab.key)}
                canDrag={canDrag}
                isDragging={draggedKey === tab.key}
                isDropTarget={dropTargetKey === tab.key && canBeDropTarget}
                onDragStart={canDrag ? (e) => handleDragStart(e, tab.key) : undefined}
                onDragOver={canBeDropTarget ? (e) => handleDragOver(e, tab.key) : undefined}
                onDrop={canBeDropTarget ? handleDrop : undefined}
                onDragEnd={handleDragEnd}
                onDragLeave={handleDragLeave}
              />
            )
          })}

          {onAdd && (
            <Box sx={sx.addTabButton(style)} onClick={onAdd} title='Add template'>
              <Iconify icon='mdi:plus' width={16} height={16} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            width: CHEVRON_W,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: hasOverflow ? 'auto' : 'none',
          }}>
          <IconButton
            size='small'
            onClick={() => scrollByAmount(1)}
            disabled={!hasOverflow || atEnd}
            sx={{
              opacity: hasOverflow ? (hover ? 1 : 0.6) : 0,
              visibility: hasOverflow && !atEnd ? 'visible' : 'hidden',
              transition: 'opacity 120ms',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'transparent' },
              boxShadow: 'none',
            }}>
            <Iconify icon='lucide:chevron-right' />
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}
